/**
 * @file This file contains the TestRunner class for running automated in-game tests.
 * @module TestRunner
 */

import { PlayerStatsManager } from '../managers/PlayerStatsManager.js';
import { AchievementManager } from '../managers/AchievementManager.js';

export class TestRunner {
    /**
     * @param {object} dependencies - The dependencies for the TestRunner.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The global event emitter.
     * @param {import('../managers/UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {import('../managers/AudioManager.js').AudioManager} dependencies.audioManager - The audio manager instance.
     * @param {AchievementManager} dependencies.mainAchievementManager - The main application's achievement manager.
     */
    constructor({ eventEmitter, ui, audioManager, mainAchievementManager }) {
        this.eventEmitter = eventEmitter;
        this.ui = ui;
        this.audioManager = audioManager;
        this.mainAchievementManager = mainAchievementManager;
    }

    /**
     * Runs a single test case in an isolated environment.
     * @param {object} testCase - The test case object to run.
     * @returns {Promise<{success: boolean, message: string}>} The result of the test.
     */
    async runTest(testCase) {
        // Disable the main achievement manager to prevent double triggers
        this.mainAchievementManager.disable();

        // 1. Create a sandboxed environment
        const testPlayerStats = new PlayerStatsManager({
            appContainer: null,
            audioManager: this.audioManager,
            eventEmitter: this.eventEmitter,
            inMemory: true // Prevent saving to localStorage
        });

        const testAchievementManager = new AchievementManager({
            playerStatsManager: testPlayerStats,
            eventEmitter: this.eventEmitter,
            ui: this.ui,
            audioManager: this.audioManager
        });

        // 2. Set up an assertion helper to listen for the achievement toast
        let toastPromiseResolver;
        const toastPromise = new Promise(resolve => {
            toastPromiseResolver = resolve;
        });

        const originalShowToast = this.ui.showAchievementToast;
        this.ui.showAchievementToast = (achievement) => {
            // We still call the original function so we can see the toast visually during tests
            originalShowToast.call(this.ui, achievement);
            toastPromiseResolver(achievement.key);
        };

        const expectToast = async (expectedKey) => {
            const timeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error(`Test timed out waiting for achievement: ${expectedKey}`)), 2000)
            );

            const triggeredKey = await Promise.race([toastPromise, timeout]);

            if (triggeredKey !== expectedKey) {
                throw new Error(`Expected achievement '${expectedKey}' but got '${triggeredKey}'`);
            }
        };

        // 3. Run the test and handle the result
        try {
            await testCase.run({ eventEmitter: this.eventEmitter, expectToast });
            return { success: true, message: 'Passed!' };
        } catch (error) {
            console.error(`Test Failed: ${testCase.name}`, error);
            return { success: false, message: error.message };
        } finally {
            // 4. Clean up by restoring the original UI method and listeners
            this.ui.showAchievementToast = originalShowToast;
            // Re-enable the main achievement manager
            this.mainAchievementManager.enable();
            testAchievementManager.destroy();
        }
    }
}